import type { Dispatch as ReduxDispatch } from '@reduxjs/toolkit';

import type { BaseAction } from '../@types/actions';
import * as http from '../Http';
import requestCache from '../utils/request-cache';

import type { GoogleMapsGeocode } from '../@types/GoogleMaps';
import { buildGetRequest } from '../Http/requestTemplates';
import { get } from '../helpers/settings';
import { getMarket } from '../i18n';

export const RCV_LOCATION = 'RCV/LOCATION_FROM_IP';
type RCV_LOCATION_TYPE = BaseAction<typeof RCV_LOCATION> & {
  response: google.maps.places.PlaceResult | undefined;
  radius: number | null;
};

export const REQ_LOCATION_FROM_POSTAL_CODE = 'REQ/LOCATION_FROM_POSTAL_CODE';
export const RCV_LOCATION_FROM_POSTAL_CODE = 'RCV/LOCATION_FROM_POSTAL_CODE';
type REQ_LOCATION_FROM_POSTAL_CODE_TYPE = BaseAction<typeof REQ_LOCATION_FROM_POSTAL_CODE>;
type RCV_LOCATION_FROM_POSTAL_CODE_TYPE = BaseAction<typeof RCV_LOCATION_FROM_POSTAL_CODE> & {
  response: google.maps.places.PlaceResult | undefined;
};

export const REQ_LOCATION_FROM_SEARCH = 'REQ/LOCATION_FROM_SEARCH';
export const RCV_LOCATION_FROM_SEARCH = 'RCV/LOCATION_FROM_SEARCH';
type REQ_LOCATION_FROM_SEARCH_TYPE = BaseAction<typeof REQ_LOCATION_FROM_SEARCH>;
type RCV_LOCATION_FROM_SEARCH_TYPE = BaseAction<typeof RCV_LOCATION_FROM_SEARCH> & {
  response: google.maps.places.PlaceResult[];
};

export const REQ_CLEAR_LOCATION_SEARCH = 'REQ/CLEAR_LOCATION_SEARCH';
type REQ_CLEAR_LOCATION_SEARCH_TYPE = BaseAction<typeof REQ_CLEAR_LOCATION_SEARCH>;

export const REQUEST_FAILED = 'LOCATION/REQUEST_FAILED';
type REQUEST_FAILED_TYPE = BaseAction<typeof REQUEST_FAILED> & {
  action: typeof REQ_LOCATION_FROM_POSTAL_CODE | typeof REQ_LOCATION_FROM_SEARCH;
};

export type GEO_ACTION =
  | RCV_LOCATION_TYPE
  | REQ_LOCATION_FROM_POSTAL_CODE_TYPE
  | RCV_LOCATION_FROM_POSTAL_CODE_TYPE
  | REQ_LOCATION_FROM_SEARCH_TYPE
  | RCV_LOCATION_FROM_SEARCH_TYPE
  | REQ_CLEAR_LOCATION_SEARCH_TYPE
  | REQUEST_FAILED_TYPE;

type Dispatch = ReduxDispatch<GEO_ACTION>;

const findIndex = (s: string[] | undefined, type: string) => {
  const index = s?.indexOf(type);
  return index !== undefined ? index > -1 : false;
};

const getLocalityResult = (results: google.maps.places.PlaceResult[]) => {
  if (results.length === 1) {
    return results[0];
  }

  return results.find((r) => findIndex(r.types, 'locality'));
};

const getPostalCodeResult = (results: google.maps.places.PlaceResult[]) =>
  results.find((r) => findIndex(r.types, 'postal_code'));

export const lookup = async (
  lat: number,
  lng: number,
  callback: (response: GoogleMapsGeocode) => void,
) => {
  try {
    const response = await http.httpJson<GoogleMapsGeocode>(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${lat},${lng}&key=${get(
        'GoogleApiKey',
      )}&language=sv`,
      buildGetRequest({ contentType: null }),
    );
    callback(response);
  } catch (_e) {
    //
  }
};

const browser =
  (lat: number, lng: number, radius: number | null = null) =>
  (dispatch: Dispatch) =>
    lookup(lat, lng, (json) =>
      dispatch({ type: RCV_LOCATION, response: getLocalityResult(json.results), radius }),
    );

const resolveComponents = () => {
  const marketCode = getMarket().marketCode;
  switch (marketCode) {
    case 'NO':
      return 'region=NO&language=no&components=country:NO';
    default:
      return 'region=SE&language=sv&components=country:SE';
  }
};

export const byPostalCode = (postalCode: string | null) => async (dispatch: Dispatch) => {
  if (requestCache.isPending(REQ_LOCATION_FROM_POSTAL_CODE)) {
    return;
  }
  requestCache.isPending(REQ_LOCATION_FROM_POSTAL_CODE, true);

  try {
    dispatch({ type: REQ_LOCATION_FROM_POSTAL_CODE });

    const response = await http.httpJson<GoogleMapsGeocode>(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${postalCode}&key=${get(
        'GoogleApiKey',
      )}&${resolveComponents()}`,
      buildGetRequest({ contentType: null }),
    );
    dispatch({
      type: RCV_LOCATION_FROM_POSTAL_CODE,
      response: getPostalCodeResult(response.results),
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_LOCATION_FROM_POSTAL_CODE });
  }
  requestCache.isPending(REQ_LOCATION_FROM_POSTAL_CODE, false);
};

export const search = (query: string) => async (dispatch: Dispatch) => {
  if (requestCache.isPending(REQ_LOCATION_FROM_SEARCH)) {
    return;
  }
  requestCache.isPending(REQ_LOCATION_FROM_SEARCH, true);

  try {
    dispatch({ type: REQ_LOCATION_FROM_SEARCH });

    const response = await http.httpJson<GoogleMapsGeocode>(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${get(
        'GoogleApiKey',
      )}&${resolveComponents()}`,
      buildGetRequest({ contentType: null }),
    );

    dispatch({
      type: RCV_LOCATION_FROM_SEARCH,
      response: response.results,
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_LOCATION_FROM_SEARCH });
  }
  requestCache.isPending(REQ_LOCATION_FROM_SEARCH, false);
};

export const clear = () => (dispatch: Dispatch) => dispatch({ type: REQ_CLEAR_LOCATION_SEARCH });

const noop = () => {};
export const current =
  (onError?: (err?: GeolocationPositionError) => void) => (dispatch: Dispatch) => {
    onError = onError || noop;
    if (!process.env.SSR && navigator && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => browser(position.coords.latitude, position.coords.longitude)(dispatch),
        onError,
      );
    } else {
      onError();
    }
  };
